import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { devtools } from 'zustand/middleware'

// Auth Store
export const useAuthStore = create(
  devtools(
    persist(
      (set, get) => ({
        // Estado
        user: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        tenant: null,

        // Actions
        login: (userData) => set({
          user: userData.user,
          token: userData.tokens.access,
          refreshToken: userData.tokens.refresh,
          isAuthenticated: true,
          tenant: userData.user.tenant
        }),

        logout: () => {
          localStorage.removeItem('auth-storage')
          set({
            user: null,
            token: null,
            refreshToken: null,
            isAuthenticated: false,
            tenant: null
          })
        },

        updateToken: (token) => set({ token }),

        setUser: (user) => set({ user }),

        switchTenant: (tenantData) => set((state) => ({
          tenant: tenantData.tenant,
          token: tenantData.tokens.access,
          refreshToken: tenantData.tokens.refresh,
          user: { ...state.user, tenant: tenantData.tenant }
        })),

        // Getters
        getToken: () => get().token,
        getUser: () => get().user,
        getTenant: () => get().tenant,
      }),
      {
        name: 'auth-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          user: state.user,
          token: state.token,
          refreshToken: state.refreshToken,
          isAuthenticated: state.isAuthenticated,
          tenant: state.tenant
        })
      }
    ),
    {
      name: 'auth-store'
    }
  )
)

// App Store
export const useAppStore = create(
  devtools(
    persist(
      (set, get) => ({
        // Layout
        sidebarOpen: true,
        darkMode: false,

      // Loading States
      loading: false,
      loadingMessage: '',

      // Notifications
      notifications: [],

      // Filters
      filters: {
        dateRange: 'today',
        department: null,
        userId: null,
      },

      // Actions
      toggleSidebar: () => set((state) => ({
        sidebarOpen: !state.sidebarOpen
      })),

      toggleDarkMode: () => {
        const newDarkMode = !get().darkMode
        set({ darkMode: newDarkMode })
        if (newDarkMode) {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
      },

      setLoading: (loading, message = '') => set({
        loading,
        loadingMessage: message
      }),

      addNotification: (notification) => {
        const id = Date.now()
        set((state) => ({
          notifications: [
            ...state.notifications,
            { ...notification, id }
          ]
        }))

        // Auto remove após 5 segundos
        setTimeout(() => {
          set((state) => ({
            notifications: state.notifications.filter(n => n.id !== id)
          }))
        }, 5000)
      },

      removeNotification: (id) => set((state) => ({
        notifications: state.notifications.filter(n => n.id !== id)
      })),

      setFilters: (filters) => set((state) => ({
        filters: { ...state.filters, ...filters }
      })),

      resetFilters: () => set({
        filters: {
          dateRange: 'today',
          department: null,
          userId: null,
        }
      })
    }),
      {
        name: 'app-storage',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          darkMode: state.darkMode,
          sidebarOpen: state.sidebarOpen
        }),
        onRehydrateStorage: () => (state) => {
          // Aplicar classe dark ao HTML quando recarregar
          if (state?.darkMode) {
            document.documentElement.classList.add('dark')
          }
        }
      }
    ),
    {
      name: 'app-store'
    }
  )
)

// Dashboard Store
export const useDashboardStore = create(
  devtools(
    (set) => ({
      // Métricas
      metrics: null,
      topApps: [],
      topUsers: [],
      activities: [],

      // Gráficos
      chartData: null,

      // Tempo Real
      realtimeUsers: [],

      // Loading States
      metricsLoading: false,
      activitiesLoading: false,

      // Actions
      setMetrics: (metrics) => set({ metrics }),
      setTopApps: (topApps) => set({ topApps }),
      setTopUsers: (topUsers) => set({ topUsers }),
      setActivities: (activities) => set({ activities }),
      setChartData: (chartData) => set({ chartData }),
      setRealtimeUsers: (users) => set({ realtimeUsers: users }),

      setMetricsLoading: (loading) => set({ metricsLoading: loading }),
      setActivitiesLoading: (loading) => set({ activitiesLoading: loading }),

      // Reset
      reset: () => set({
        metrics: null,
        topApps: [],
        topUsers: [],
        activities: [],
        chartData: null,
        realtimeUsers: [],
        metricsLoading: false,
        activitiesLoading: false,
      })
    }),
    {
      name: 'dashboard-store'
    }
  )
)
